import type { CdnItem } from '@/types';

import React from 'react';
import { RiEdit2Line, RiDeleteBinLine, RiAddLine, RiCodeLine, RiLink } from '@remixicon/react';
import { Button, Chip, Tooltip } from '@heroui/react';
import { useAtom, useAtomValue } from 'jotai';
import { PermissionGuard } from '@/components/shared/PermissionGuard';

import { TablePage, TableFilters, ColumnsToggleDropdown } from '@/components/table';
import { cdnListParamsAtom, cdnQueryAtom, setDefaultCdn } from '@/store';
import { useDisclosure } from '@heroui/react';
import { toast } from 'react-toastify';

// Custom hooks
import { useCdnFilters, useCdnSorting, useCdnForm, useCdnDelete } from '@/hooks/cdn';
import { useVisibleColumns } from '@/hooks/table';

// Components
import { CdnFormModal } from '@/components/cdn/CdnFormModal';
import { ConfirmDeleteModal } from '@/components/shared/ConfirmDeleteModal';
import { SearchInput } from '@/components/shared/SearchInput';

export default function CdnPage() {
  const { data, isLoading, isError, error } = useAtomValue(cdnQueryAtom);
  const [params, setParams] = useAtom(cdnListParamsAtom);

  // Custom hooks
  const { searchTerm, setSearchTerm, clearFilters, handlePageChange, hasAnyFilter, handleSearch, handleClearSearch } =
    useCdnFilters();

  const {
    isFormOpen,
    isSubmitting,
    formData,
    setFormData,
    openCreate,
    openEdit,
    saveForm,
    onFormOpenChange,
    title,
    submitText
  } = useCdnForm();
  const { isDeleteOpen, deletingCdnId, isDeleting, openDelete, confirmDelete, onDeleteOpenChange } = useCdnDelete();

  // Set default modal state
  const { isOpen: isSetDefaultOpen, onOpen: onSetDefaultOpen, onOpenChange: onSetDefaultOpenChange } = useDisclosure();
  const [defaultCdnId, setDefaultCdnId] = React.useState<number | null>(null);
  const [, setDefaultCdnAtom] = useAtom(setDefaultCdn);

  const cdns = React.useMemo<CdnItem[]>(() => data?.data ?? [], [data]);
  const { sortDescriptor, setSortDescriptor, sortedItems } = useCdnSorting(cdns);

  // Function to trigger data refresh
  const triggerRefresh = React.useCallback(() => {
    setParams((prev) => ({ ...prev, refresh: Date.now() }));
  }, [setParams]);

  // Set default handler
  const handleSetDefault = React.useCallback(async () => {
    if (!defaultCdnId) return;

    try {
      await setDefaultCdnAtom(defaultCdnId);
      toast.success('CDN set as default successfully');
      onSetDefaultOpenChange();
      setDefaultCdnId(null);
      triggerRefresh();
    } catch (error) {
      // Error handled by axios interceptor
    }
  }, [defaultCdnId, setDefaultCdnAtom, onSetDefaultOpenChange, triggerRefresh]);

  // Open set default modal
  const openSetDefault = React.useCallback(
    (cdnId: number) => {
      setDefaultCdnId(cdnId);
      onSetDefaultOpen();
    },
    [onSetDefaultOpen]
  );

  // Enhanced save functions
  const handleSaveForm = React.useCallback(async () => {
    await saveForm();
  }, [saveForm]);

  const handleConfirmDelete = React.useCallback(async () => {
    await confirmDelete();
  }, [confirmDelete]);

  const renderCell = React.useCallback(
    (cdn: CdnItem, columnKey: React.Key) => {
      switch (columnKey) {
        case 'name':
          return (
            <div className="flex items-center gap-2">
              <RiLink className="text-primary h-4 w-4" />
              <span className="font-medium">{cdn.name}</span>
            </div>
          );
        case 'cdn_tag':
          return (
            <div className="flex items-center gap-2">
              <Chip
                color={
                  cdn.cdn_tag === 'stable'
                    ? 'success'
                    : cdn.cdn_tag === 'latest'
                      ? 'warning'
                      : cdn.cdn_tag === 'dev'
                        ? 'primary'
                        : cdn.cdn_tag === 'beta'
                          ? 'secondary'
                          : 'default'
                }
                variant="flat"
              >
                {cdn.cdn_tag}
              </Chip>
            </div>
          );
        case 'provider':
          return (
            <span className="flex items-center gap-2">
              <RiLink className="text-primary h-4 w-4" />
              <span className="text-default-600 text-sm">{cdn.provider?.name || '-'}</span>
            </span>
          );
        case 'version':
          return <span className="text-default-600 text-sm">{cdn.version || '-'}</span>;
        case 'cdn_url':
          return <span className="text-default-600 max-w-[200px] truncate text-sm">{cdn.cdn_url || '-'}</span>;
        case 'type':
          return (
            <div className="flex items-center gap-2">
              {cdn.is_default ? (
                <Chip color="success" size="sm" variant="flat">
                  Default
                </Chip>
              ) : (
                <PermissionGuard permission="Cdn.update">
                  <Button size="sm" variant="light" color="primary" onPress={() => openSetDefault(cdn.id)}>
                    Set Default
                  </Button>
                </PermissionGuard>
              )}
            </div>
          );
        case 'actions':
          return (
            <div className="relative flex items-center justify-end">
              <PermissionGuard permission="Cdn.update">
                <Tooltip content="Edit CDN">
                  <Button isIconOnly size="sm" variant="light" onPress={() => openEdit(cdn)}>
                    <RiEdit2Line className="h-4 w-4" />
                  </Button>
                </Tooltip>
              </PermissionGuard>
              <PermissionGuard permission="Cdn.destroy">
                <Tooltip content="Delete CDN">
                  <Button isIconOnly size="sm" variant="light" color="danger" onPress={() => openDelete(cdn)}>
                    <RiDeleteBinLine className="h-4 w-4" />
                  </Button>
                </Tooltip>
              </PermissionGuard>
            </div>
          );
        default:
          return null;
      }
    },
    [openEdit, openDelete, openSetDefault]
  );

  const columns = React.useMemo(
    () => [
      { name: 'Name', uid: 'name', sortable: true },
      { name: 'Provider', uid: 'provider', sortable: true },
      { name: 'CDN Tag', uid: 'cdn_tag', sortable: true },
      { name: 'Version', uid: 'version', sortable: true },
      { name: 'CDN URL', uid: 'cdn_url', sortable: true },
      { name: 'Type', uid: 'type', sortable: false },
      { name: 'Actions', uid: 'actions', align: 'end' }
    ],
    []
  );

  // Columns visibility management
  const { visibleColumns, setVisibleColumns, headerColumns } = useVisibleColumns(columns);

  return (
    <TablePage
      columns={columns}
      currentPage={data?.meta?.current_page}
      data={sortedItems}
      emptyMessage="No CDNs found"
      error={error}
      errorMessage="Failed to load CDNs"
      filtersSection={
        <TableFilters
          hasActiveFilters={hasAnyFilter}
          isRefreshing={isLoading}
          onClearFilters={clearFilters}
          onRefresh={triggerRefresh}
        >
          <SearchInput
            placeholder="Search CDNs..."
            value={searchTerm}
            onValueChange={setSearchTerm}
            onSearch={handleSearch}
            onClear={handleClearSearch}
          />

          <ColumnsToggleDropdown
            allColumns={columns.map((c) => ({ uid: c.uid, name: c.name }))}
            selected={visibleColumns}
            onChange={(next) => setVisibleColumns(next)}
          />
        </TableFilters>
      }
      headerColumns={headerColumns}
      isError={isError}
      isLoading={isLoading}
      renderCell={renderCell}
      sortDescriptor={sortDescriptor || { column: 'cdn_tag', direction: 'ascending' }}
      title="CDN Management"
      titleActions={
        <Button
          color="success"
          size="sm"
          variant="flat"
          startContent={<RiAddLine className="h-4 w-4" />}
          onPress={openCreate}
        >
          Add CDN
        </Button>
      }
      totalPages={data?.meta?.last_page}
      onPageChange={handlePageChange}
      onRefresh={triggerRefresh}
      onSortChange={setSortDescriptor}
    >
      {/* CDN Form Modal */}
      <CdnFormModal
        isOpen={isFormOpen}
        onOpenChange={onFormOpenChange}
        formData={formData}
        setFormData={setFormData}
        isSubmitting={isSubmitting}
        onSubmit={handleSaveForm}
        title={title}
        submitText={submitText}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={isDeleteOpen}
        onOpenChange={onDeleteOpenChange}
        title="Delete CDN"
        message={`Are you sure you want to delete this CDN? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        isLoading={isDeleting}
        onConfirm={handleConfirmDelete}
      />

      {/* Set Default Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={isSetDefaultOpen}
        onOpenChange={onSetDefaultOpenChange}
        title="Set Default CDN"
        message="Are you sure you want to set this CDN as the default? This will make it the primary CDN for new domains."
        confirmText="Set Default"
        cancelText="Cancel"
        isLoading={false}
        onConfirm={handleSetDefault}
      />
    </TablePage>
  );
}
